import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useEffect, useRef, useState, useCallback } from 'react';
import { trackEvent } from '../services/analytics';
import './HomePage.css';

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [navScrolled, setNavScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Sticky nav shadow on scroll
  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // IntersectionObserver for fade-in sections
  const observerRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll('.lp-section').forEach((el) => {
      observerRef.current?.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleCta = useCallback((label: string, target: string) => {
    trackEvent('cta_click', { label, target });
    navigate(target);
  }, [navigate]);

  // Feature data
  const features = [
    { icon: '\uD83D\uDDE3\uFE0F', title: t('home.features.checkIn'), desc: t('home.features.checkInDesc') },
    { icon: '\uD83D\uDCDD', title: t('home.features.resumeTuner'), desc: t('home.features.resumeTunerDesc') },
    { icon: '\uD83E\uDDE0', title: t('home.features.careerMemory'), desc: t('home.features.careerMemoryDesc') },
    { icon: '\uD83C\uDFAF', title: t('home.features.careerVision'), desc: t('home.features.careerVisionDesc') },
    { icon: '\uD83D\uDCF1', title: t('home.features.linkedin'), desc: t('home.features.linkedinDesc') },
    { icon: '\uD83D\uDD0D', title: t('home.features.jobSearch'), desc: t('home.features.jobSearchDesc') },
  ];

  // Pricing data
  const freeFeatures = [
    t('home.pricing.freeFeatures.conversations'),
    t('home.pricing.freeFeatures.resumeTunes'),
    t('home.pricing.freeFeatures.linkedinPosts'),
    t('home.pricing.freeFeatures.careerMemory'),
    t('home.pricing.freeFeatures.careerVision'),
    t('home.pricing.freeFeatures.jobSearch'),
    t('home.pricing.freeFeatures.voiceMode'),
  ];
  const proFeatures = [
    t('home.pricing.proFeatures.unlimited'),
    t('home.pricing.proFeatures.priority'),
    t('home.pricing.proFeatures.analytics'),
    t('home.pricing.proFeatures.support'),
    t('home.pricing.proFeatures.export'),
  ];

  // FAQ data
  const faqs = [
    { q: t('home.faq.q1'), a: t('home.faq.a1') },
    { q: t('home.faq.q2'), a: t('home.faq.a2') },
    { q: t('home.faq.q3'), a: t('home.faq.a3') },
    { q: t('home.faq.q4'), a: t('home.faq.a4') },
    { q: t('home.faq.q5'), a: t('home.faq.a5') },
  ];

  return (
    <div style={{ fontFamily: 'var(--font-sans)' }}>

      {/* ===== Sticky Nav ===== */}
      <nav className={`lp-nav ${navScrolled ? 'scrolled' : ''}`}>
        <span className="lp-nav-logo">My Job Odyssey <span className="lp-beta-badge">{t('home.beta')}</span></span>
        <div className="lp-nav-links">
          <a href="#features" onClick={(e) => { e.preventDefault(); scrollTo('features'); }}>
            {t('home.nav.features')}
          </a>
          <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo('how-it-works'); }}>
            {t('home.nav.howItWorks')}
          </a>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }}>
            {t('home.nav.pricing')}
          </a>
          <a href="#faq" onClick={(e) => { e.preventDefault(); scrollTo('faq'); }}>
            {t('home.nav.faq')}
          </a>
        </div>
        <div className="lp-nav-right">
          <LanguageSwitcher />
          <button className="lp-nav-cta" onClick={() => handleCta('nav_get_started', '/signup')}>
            {t('auth.signup')}
          </button>
        </div>
      </nav>

      {/* ===== Hero ===== */}
      <section className="lp-hero">
        <h1>{t('home.hero.headline')}</h1>
        <p className="lp-hero-sub">{t('home.hero.subheadline')}</p>
        <p className="lp-hero-beta">{t('home.hero.betaNotice')}</p>
        <div className="lp-hero-buttons">
          <button className="lp-btn-primary" onClick={() => handleCta('hero_get_started', '/signup')}>
            {t('home.hero.cta')}
          </button>
          <button className="lp-btn-secondary" onClick={() => scrollTo('features')}>
            {t('home.hero.ctaSecondary')} ↓
          </button>
        </div>
      </section>

      {/* ===== Social Proof ===== */}
      <div className="lp-proof">
        <div className="lp-proof-badge">
          <span>✦</span> {t('home.proof.gemini')}
        </div>
        <div className="lp-proof-badge">
          <span>🌐</span> {t('home.proof.bilingual')}
        </div>
        <div className="lp-proof-badge">
          <span>🎙</span> {t('home.proof.voice')}
        </div>
      </div>

      {/* ===== Features ===== */}
      <section id="features" className="lp-features lp-section">
        <div className="lp-section-header">
          <h2>{t('home.features.sectionTitle')}</h2>
          <p>{t('home.features.sectionSubtitle')}</p>
        </div>
        <div className="lp-features-grid">
          {features.map((f, i) => (
            <div key={i} className="lp-feature-card" onClick={() => trackEvent('feature_click', { feature: f.title })}>
              <div className="lp-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== How It Works ===== */}
      <section id="how-it-works" className="lp-how lp-section">
        <div className="lp-how-inner">
          <div className="lp-section-header">
            <h2>{t('home.howItWorks.sectionTitle')}</h2>
            <p>{t('home.howItWorks.sectionSubtitle')}</p>
          </div>
          <div className="lp-steps">
            <div className="lp-step">
              <div className="lp-step-number">1</div>
              <h3>{t('home.howItWorks.step1Title')}</h3>
              <p>{t('home.howItWorks.step1Desc')}</p>
            </div>
            <div className="lp-step">
              <div className="lp-step-number">2</div>
              <h3>{t('home.howItWorks.step2Title')}</h3>
              <p>{t('home.howItWorks.step2Desc')}</p>
            </div>
            <div className="lp-step">
              <div className="lp-step-number">3</div>
              <h3>{t('home.howItWorks.step3Title')}</h3>
              <p>{t('home.howItWorks.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Pricing ===== */}
      <section id="pricing" className="lp-pricing lp-section">
        <div className="lp-section-header">
          <h2>{t('home.pricing.sectionTitle')}</h2>
          <p>{t('home.pricing.sectionSubtitle')}</p>
        </div>
        <div className="lp-pricing-grid">
          {/* Free */}
          <div className="lp-price-card featured">
            <div className="lp-price-name">{t('home.pricing.free')}</div>
            <div className="lp-price-amount">
              <span>{t('home.pricing.freePrice')}</span>
              <span>{t('home.pricing.freePeriod')}</span>
            </div>
            <ul className="lp-price-features">
              {freeFeatures.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
            <button className="lp-price-cta primary" onClick={() => handleCta('pricing_free', '/signup')}>
              {t('home.pricing.freeCta')}
            </button>
          </div>
          {/* Pro */}
          <div className="lp-price-card disabled">
            <span className="lp-price-badge coming-soon">{t('home.pricing.proBadge')}</span>
            <div className="lp-price-name">{t('home.pricing.pro')}</div>
            <div className="lp-price-amount">
              <span>{t('home.pricing.proPrice')}</span>
              <span>{t('home.pricing.proPeriod')}</span>
            </div>
            <ul className="lp-price-features">
              {proFeatures.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
            <button className="lp-price-cta disabled" disabled>
              {t('home.pricing.proCta')}
            </button>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="lp-faq lp-section">
        <div className="lp-section-header">
          <h2>{t('home.faq.sectionTitle')}</h2>
        </div>
        <div className="lp-faq-list">
          {faqs.map((faq, i) => (
            <div key={i} className="lp-faq-item">
              <button className="lp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                {faq.q}
                <span className={`lp-faq-arrow ${openFaq === i ? 'open' : ''}`}>▾</span>
              </button>
              <div className={`lp-faq-a ${openFaq === i ? 'open' : ''}`}>
                {faq.a}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="lp-final-cta lp-section">
        <h2>{t('home.finalCta.headline')}</h2>
        <p>{t('home.finalCta.subheadline')}</p>
        <button className="lp-btn-primary" onClick={() => handleCta('final_cta', '/signup')}>
          {t('home.finalCta.cta')}
        </button>
      </section>

      {/* ===== Footer ===== */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <h3>My Job Odyssey</h3>
            <p>{t('home.footer.tagline')}</p>
          </div>
          <div className="lp-footer-col">
            <h4>{t('home.footer.product')}</h4>
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollTo('features'); }}>
              {t('home.nav.features')}
            </a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }}>
              {t('home.nav.pricing')}
            </a>
            <a href="#faq" onClick={(e) => { e.preventDefault(); scrollTo('faq'); }}>
              {t('home.nav.faq')}
            </a>
          </div>
          <div className="lp-footer-col">
            <h4>{t('home.footer.legal')}</h4>
            <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>
              {t('home.footer.privacy')}
            </a>
            <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>
              {t('home.footer.terms')}
            </a>
          </div>
        </div>
        <div className="lp-footer-bottom">
          © {new Date().getFullYear()} {t('home.footer.copyright')}
        </div>
      </footer>
    </div>
  );
}
